import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

/** A named set of entrances summed into a single tile on the modern view. */
export interface EntranceGroup {
  id: string;
  name: string;
  entranceNames: string[];
}

interface DraftGroup {
  id: string;
  name: string;
  entranceNames: string[];
  nameError: boolean;
}

/**
 * Presentational modal for creating "Entrance Groups" by dragging entrances onto
 * named groups. Mirrors the gate-groups feature on the performance-testing page;
 * it derives its working copy from the inputs and raises a single `save` output
 * rather than persisting anything itself — the parent (modern-summary) owns
 * persistence and the summing.
 */
@Component({
  selector: 'app-entrance-groups',
  standalone: false,
  templateUrl: './entrance-groups.component.html',
  styleUrls: ['./entrance-groups.component.scss'],
})
export class EntranceGroupsComponent implements OnChanges {
  /** Every entrance key for the venue, so all are assignable even at zero count. */
  @Input() entranceNames: string[] = [];
  @Input() existingGroups: EntranceGroup[] = [];

  @Output() save = new EventEmitter<EntranceGroup[]>();
  @Output() cancel = new EventEmitter<void>();

  groups: DraftGroup[] = [];
  ungrouped: string[] = [];

  /** Free-text filter applied to the ungrouped column. */
  ungroupedFilter: string = '';
  /**
   * The ungrouped entrances matching {@link ungroupedFilter}. Held as a field
   * (recomputed via {@link applyFilter}) rather than a getter so `*ngFor` and the
   * CDK drop list see a stable array reference between change-detection cycles.
   */
  filteredUngrouped: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entranceNames'] || changes['existingGroups']) {
      this.rebuildFromInputs();
    }
  }

  /**
   * Builds the editable working copy from the inputs. Group membership is
   * filtered to entrances that still exist for the venue, and anything left over
   * lands in the ungrouped column.
   */
  private rebuildFromInputs(): void {
    const used = new Set<string>();
    this.groups = (this.existingGroups || []).map((g) => {
      const filtered = (g.entranceNames || []).filter((n) => this.entranceNames.includes(n));
      filtered.forEach((n) => used.add(n));
      return {
        id: g.id,
        name: g.name,
        entranceNames: filtered,
        nameError: false,
      };
    });
    this.ungrouped = this.entranceNames.filter((n) => !used.has(n));
    this.applyFilter();
  }

  /** Recomputes {@link filteredUngrouped} from the current pool and filter text. */
  applyFilter(): void {
    const query = this.ungroupedFilter.trim().toLowerCase();
    this.filteredUngrouped = query
      ? this.ungrouped.filter((n) => n.toLowerCase().includes(query))
      : [...this.ungrouped];
  }

  clearFilter(): void {
    this.ungroupedFilter = '';
    this.applyFilter();
  }

  /** True once a filter is narrowing the pool — gates the bulk-move affordance. */
  get isFiltering(): boolean {
    return !!this.ungroupedFilter.trim();
  }

  trackGroup(_idx: number, g: DraftGroup): string {
    return g.id;
  }

  trackEntrance(_idx: number, n: string): string {
    return n;
  }

  groupListIds(): string[] {
    return this.groups.map((g) => g.id);
  }

  addGroup(): void {
    const id = 'eg_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.groups = [...this.groups, { id, name: '', entranceNames: [], nameError: false }];
  }

  removeGroup(group: DraftGroup): void {
    // Return the group's entrances to the ungrouped column, preserving order.
    const restored = [...this.ungrouped, ...group.entranceNames];
    this.ungrouped = this.entranceNames.filter((n) => restored.includes(n));
    this.groups = this.groups.filter((g) => g.id !== group.id);
    this.applyFilter();
  }

  /** The CDK drop-list id used by the ungrouped column. */
  static readonly UNGROUPED_LIST_ID = 'eg-ungrouped';

  /**
   * Moves the dragged entrance between lists by *value*, not by index. The
   * ungrouped column renders a *filtered* view, so the CDK drop index points into
   * the filtered list rather than the backing arrays — an index-based transfer
   * would move the wrong item. The ungrouped list's logical array is always the
   * full {@code ungrouped} (the filtered list is just its view); group lists use
   * their own {@code entranceNames}.
   */
  onDrop(event: CdkDragDrop<string[]>): void {
    const entrance: string = event.item.data;
    const source = this.logicalListFor(event.previousContainer.id, event.previousContainer.data);
    const target = this.logicalListFor(event.container.id, event.container.data);

    if (source === target) {
      // No cross-list move; reordering a filtered view is ambiguous, so ignore it.
      return;
    }

    const sourceIdx = source.indexOf(entrance);
    if (sourceIdx === -1) return;
    source.splice(sourceIdx, 1);
    if (!target.includes(entrance)) {
      target.push(entrance);
    }
    this.applyFilter();
  }

  /** Resolves a drop list's id to the backing array it actually mutates. */
  private logicalListFor(listId: string, fallback: string[]): string[] {
    return listId === EntranceGroupsComponent.UNGROUPED_LIST_ID ? this.ungrouped : fallback;
  }

  /**
   * Moves every currently-filtered ungrouped entrance into the given group in
   * one action. Lets the user filter (e.g. "north") and assign the whole match
   * set without dragging each chip.
   */
  addFilteredToGroup(group: DraftGroup): void {
    const toMove = [...this.filteredUngrouped];
    if (!toMove.length) return;
    const moving = new Set(toMove);
    this.ungrouped = this.ungrouped.filter((n) => !moving.has(n));
    for (const name of toMove) {
      if (!group.entranceNames.includes(name)) {
        group.entranceNames.push(name);
      }
    }
    this.applyFilter();
  }

  /** Every group needs a non-empty, case-insensitively unique name. */
  private validate(): boolean {
    let ok = true;
    const seen = new Set<string>();
    for (const g of this.groups) {
      const trimmed = g.name.trim();
      const duplicate = !!trimmed && seen.has(trimmed.toLowerCase());
      if (!trimmed || duplicate) {
        g.nameError = true;
        ok = false;
      } else {
        g.nameError = false;
        seen.add(trimmed.toLowerCase());
      }
    }
    return ok;
  }

  onSave(): void {
    if (!this.validate()) {
      return;
    }
    // Drop empty groups so a half-finished group doesn't persist.
    const out: EntranceGroup[] = this.groups
      .filter((g) => g.entranceNames.length > 0)
      .map((g) => ({ id: g.id, name: g.name.trim(), entranceNames: [...g.entranceNames] }));
    this.save.emit(out);
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
